/**
 * Offerte Portaal View - Dezelfde layout als OfferteDetail maar voor klantenportaal
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../../services/api';
import { Bedrijfsgegevens } from '../../services/bedrijfsgegevens.service';
import Toast from '../../components/Toast';
import '../offertes/OfferteDetail.css';

interface OfferteRegel {
  id: number;
  regel_nummer: number;
  omschrijving: string;
  aantal: number;
  eenheid: string;
  eenheidsprijs: number;
  btw_percentage: number;
  totaal: number;
  calculatie_id: number | null;
  type_regel: string;
}

interface OfferteCalculatie {
  id: number;
  naam: string;
  omschrijving: string | null;
  subtotaal: number;
  regels: OfferteRegel[];
}

interface Offerte {
  id: number;
  offertenummer: string;
  status: string;
  offerte_datum: string;
  geldig_tot: string;
  relatie_id: number;
  boot_id: number | null;
  onderwerp: string | null;
  omschrijving: string | null;
  subtotaal: number;
  btw_percentage: number;
  btw_bedrag: number;
  totaal: number;
  calculaties: OfferteCalculatie[];
  regels: OfferteRegel[];
  goedgekeurd_op?: string;
  goedgekeurd_door?: string;
}

interface Klant {
  bedrijfsnaam: string;
  aanhef?: string;
  voornaam?: string;
  tussenvoegsel?: string;
  achternaam?: string;
  straat?: string;
  huisnummer?: string;
  postcode?: string;
  plaats?: string;
}

const OffertePortaalView = () => {
  const { token, offerteId } = useParams<{ token: string; offerteId: string }>();
  const navigate = useNavigate();
  const [offerte, setOfferte] = useState<Offerte | null>(null);
  const [bedrijf, setBedrijf] = useState<Bedrijfsgegevens | null>(null);
  const [klant, setKlant] = useState<Klant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logoError, setLogoError] = useState(false);

  // Goedkeurings state
  const [showGoedkeurModal, setShowGoedkeurModal] = useState(false);
  const [goedkeurNaam, setGoedkeurNaam] = useState('');
  const [voorwaardenAkkoord, setVoorwaardenAkkoord] = useState(false);
  const [goedkeurLoading, setGoedkeurLoading] = useState(false);
  const [goedkeurError, setGoedkeurError] = useState<string | null>(null);
  const [goedkeurSuccess, setGoedkeurSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (token && offerteId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, offerteId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const baseUrl = getApiBaseUrl();

      // Laad offerte via portaal endpoint
      const offerteResponse = await fetch(`${baseUrl}/api/v1/klant-portaal/offerte/${token}/${offerteId}/detail`);
      if (!offerteResponse.ok) {
        const errorData = await offerteResponse.json();
        throw new Error(errorData.detail || 'Kon offerte niet laden');
      }
      const offerteData = await offerteResponse.json();
      setOfferte(offerteData);

      // Laad bedrijfsgegevens (publiek endpoint)
      const bedrijfResponse = await fetch(`${baseUrl}/api/v1/bedrijfsgegevens/public`);
      if (bedrijfResponse.ok) {
        setBedrijf(await bedrijfResponse.json());
      }

      // Klant data zit in offerte response
      if (offerteData.relatie) {
        setKlant(offerteData.relatie);
      }

      setError(null);
    } catch (err: any) {
      setError(err.message || 'Er is een fout opgetreden');
    } finally {
      setLoading(false);
    }
  };

  const handleGoedkeuren = async () => {
    if (!goedkeurNaam.trim()) {
      setGoedkeurError('Vul uw naam in');
      return;
    }
    if (!voorwaardenAkkoord) {
      setGoedkeurError('U moet akkoord gaan met de voorwaarden');
      return;
    }

    try {
      setGoedkeurLoading(true);
      setGoedkeurError(null);
      const baseUrl = getApiBaseUrl();

      const response = await fetch(`${baseUrl}/api/v1/klant-portaal/offerte/${token}/${offerteId}/goedkeuren`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          naam: goedkeurNaam,
          voorwaarden_akkoord: voorwaardenAkkoord
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Fout bij goedkeuren');
      }

      setGoedkeurSuccess('Offerte succesvol goedgekeurd! Bedankt voor uw goedkeuring. Wij nemen zo spoedig mogelijk contact met u op.');
      setShowGoedkeurModal(false);
      // Herlaad offerte data
      loadData();
    } catch (err: any) {
      setGoedkeurError(err.message || 'Er is een fout opgetreden');
    } finally {
      setGoedkeurLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getKlantNaam = () => {
    if (!klant) return '';
    if (klant.bedrijfsnaam === 'Particulier') {
      const naamParts = [
        klant.aanhef,
        klant.voornaam ? `${klant.voornaam.charAt(0)}.` : undefined,
        klant.tussenvoegsel,
        klant.achternaam
      ].filter(Boolean);
      return naamParts.join(' ');
    }
    return klant.bedrijfsnaam;
  };

  if (loading) {
    return (
      <div className="offerte-detail-container">
        <div className="loading">Offerte laden...</div>
      </div>
    );
  }

  if (error || !offerte) {
    return (
      <div className="offerte-detail-container">
        <div className="error-message">{error || 'Offerte niet gevonden'}</div>
        <button className="btn btn-secondary" onClick={() => navigate(`/portaal/${token}`)}>
          Terug naar portaal
        </button>
      </div>
    );
  }

  // Render functies identiek aan OfferteDetail
  const renderCompanyHeader = () => {
    const logoUrl = `${getApiBaseUrl()}/api/v1/files/logo`;

    return (
      <div className="offerte-header">
        <div className="header-left">
          {!logoError ? (
            <img
              src={logoUrl}
              alt={bedrijf?.bedrijfsnaam || 'Logo'}
              className="company-logo"
              onError={() => setLogoError(true)}
            />
          ) : (
            <div className="company-name-large">
              <span className="buro">Buro</span>
              <span className="vier">42</span>
            </div>
          )}
        </div>

        <div className="header-right">
          <div className="company-details">
            <strong>{bedrijf?.bedrijfsnaam || 'Buro42'}</strong>
            {bedrijf?.straat && bedrijf?.huisnummer && (
              <div>{bedrijf.straat} {bedrijf.huisnummer}</div>
            )}
            {bedrijf?.postcode && bedrijf?.plaats && (
              <div>{bedrijf.postcode} {bedrijf.plaats}</div>
            )}
            <div className="spacer"></div>
            {bedrijf?.telefoon && <div>Tel. {bedrijf.telefoon}</div>}
            {bedrijf?.email && <div>E-mail. {bedrijf.email}</div>}
            {bedrijf?.website && <div>Web. {bedrijf.website}</div>}
            <div className="spacer"></div>
            {bedrijf?.kvk_nummer && <div>KvK-nummer {bedrijf.kvk_nummer}</div>}
            {bedrijf?.btw_nummer && <div>Btw-id. {bedrijf.btw_nummer}</div>}
          </div>
        </div>
      </div>
    );
  };

  const renderHeaderCompact = (pageNum: number, totalPages: number) => (
    <>
      {renderCompanyHeader()}
      <div className="offerte-info-small">
        <div><strong>Kostenraming:</strong> {offerte.offertenummer}</div>
        <div><strong>Pagina:</strong> {pageNum} van {totalPages}</div>
      </div>
    </>
  );

  const renderHeader = (totalPages: number) => (
    <>
      {renderCompanyHeader()}

      {/* Klantgegevens */}
      <div className="customer-details">
        <strong>{getKlantNaam()}</strong>
        {klant?.straat && klant?.huisnummer && (
          <div>{klant.straat} {klant.huisnummer}</div>
        )}
        {klant?.postcode && klant?.plaats && (
          <div>{klant.postcode} {klant.plaats}</div>
        )}
      </div>

      {/* Offerte titel en info */}
      <div className="offerte-title-section">
        <h1>
          <span className="offerte-title">Kostenraming</span>
          <span className="offerte-onderwerp">{offerte.onderwerp}</span>
        </h1>
        <div className="offerte-info">
          <div><strong>Nummer:</strong> {offerte.offertenummer}</div>
          <div><strong>Datum:</strong> {formatDate(offerte.offerte_datum)}</div>
          <div><strong>Geldig tot:</strong> {formatDate(offerte.geldig_tot)}</div>
          {totalPages > 1 && <div><strong>Pagina:</strong> 1 van {totalPages}</div>}
        </div>
      </div>

      {/* Omschrijving */}
      {offerte.omschrijving && (
        <div className="offerte-description">
          {offerte.omschrijving}
        </div>
      )}
    </>
  );

  const renderCalculatie = (calculatie: OfferteCalculatie, index: number) => {
    const arbeidRegels = calculatie.regels.filter((r) => r.type_regel === 'ARBEID');
    const materiaalRegels = calculatie.regels.filter((r) => r.type_regel === 'MATERIAAL' || r.type_regel === 'ONDERDEEL' || r.type_regel === 'UITBESTEED' || r.type_regel === 'TEKST');

    const isLastPage = offerte.calculaties && index === offerte.calculaties.length - 1;

    return (
      <>
        <div className="calculatie-section">
          <h2 className="calculatie-title">Sectie: {calculatie.naam}</h2>
          {calculatie.omschrijving && (
            <div className="calculatie-description">{calculatie.omschrijving}</div>
          )}

          {/* Werkzaamheden */}
          {arbeidRegels.length > 0 && (
            <div className="offerte-section">
              <h3>Werkzaamheden</h3>
              <div className="regels-list">
                {arbeidRegels.map((regel, idx) => (
                  <div key={idx} className="regel-item">
                    <div className="regel-details">
                      <span className="regel-aantal">{regel.aantal}</span>
                      <span className="regel-eenheid">{regel.eenheid}</span>
                      <span className="regel-omschrijving">{regel.omschrijving}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="section-total">
                <span>Subtotaal excl. BTW</span>
                <strong>{formatCurrency(arbeidRegels.reduce((sum, r) => {
                  const totaal = typeof r.totaal === 'number' ? r.totaal : parseFloat(String(r.totaal || 0));
                  return sum + totaal;
                }, 0))}</strong>
              </div>
            </div>
          )}

          {/* Materialen */}
          {materiaalRegels.length > 0 && (
            <div className="offerte-section">
              <h3>{materiaalRegels.some((r) => r.type_regel === 'UITBESTEED') ? 'Materialen en Uitbesteed werk' : 'Materialen'}</h3>
              <div className="regels-list">
                {materiaalRegels.map((regel, idx) => (
                  <div key={idx} className="regel-item">
                    {regel.type_regel === 'TEKST' ? (
                      <div className="regel-tekst">{regel.omschrijving}</div>
                    ) : (
                      <div className="regel-details">
                        <span className="regel-aantal">{regel.aantal}</span>
                        <span className="regel-eenheid">{regel.eenheid}</span>
                        <span className="regel-omschrijving">{regel.omschrijving}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="section-total">
                <span>Subtotaal excl. BTW</span>
                <strong>{formatCurrency(materiaalRegels.filter((r) => r.type_regel !== 'TEKST').reduce((sum, r) => {
                  const totaal = typeof r.totaal === 'number' ? r.totaal : parseFloat(String(r.totaal || 0));
                  return sum + totaal;
                }, 0))}</strong>
              </div>
            </div>
          )}

          {/* Subtotaal */}
          <div className="calculatie-subtotal">
            <span>Totaal {calculatie.naam} excl. BTW</span>
            <strong>{formatCurrency(calculatie.subtotaal)}</strong>
          </div>
        </div>

        {/* Als laatste calculatie op pagina 1, toon ook totalen */}
        {isLastPage && offerte.calculaties && offerte.calculaties.length === 1 && (
          <div className="totalen-wrapper">
            {renderTotalen()}
            {renderFooter()}
          </div>
        )}
      </>
    );
  };

  const renderTotalen = () => (
    <div className="offerte-totals">
      <div className="total-box">
        <div className="total-label">Totaalprijs excl. BTW:</div>
        <div className="total-amount">{formatCurrency(offerte.subtotaal)}</div>
      </div>
      <div className="total-box">
        <div className="total-label">Totaal aan BTW {Math.round(offerte.btw_percentage)}%:</div>
        <div className="total-amount">{formatCurrency(offerte.btw_bedrag)}</div>
      </div>
      <div className="total-box total-final">
        <div className="total-label">Totaal prijs incl. BTW:</div>
        <div className="total-amount">{formatCurrency(offerte.totaal)}</div>
      </div>
    </div>
  );

  const renderFooter = () => (
    <>
      {bedrijf?.factuur_footer && (
        <div className="offerte-footer">
          {bedrijf.factuur_footer}
        </div>
      )}
    </>
  );

  // Splits calculaties op in pagina's
  const renderPages = () => {
    const pages = [];
    const calculaties = offerte.calculaties && offerte.calculaties.length > 0 ? offerte.calculaties : [];

    // Bereken totaal aantal pagina's
    const totalPages = calculaties.length > 0 ? calculaties.length : 1;

    // Pagina 1: Header + eerste calculatie
    pages.push(
      <div key="page-1" className="offerte-document">
        {renderHeader(totalPages)}
        {calculaties.length > 0 && renderCalculatie(calculaties[0], 0)}
      </div>
    );

    // Overige calculaties elk op eigen pagina
    for (let i = 1; i < calculaties.length; i++) {
      const isLastPage = i === calculaties.length - 1;
      pages.push(
        <div key={`page-${i + 1}`} className="offerte-document">
          {renderHeaderCompact(i + 1, totalPages)}
          {renderCalculatie(calculaties[i], i)}
          {/* Totalen op laatste pagina */}
          {isLastPage && (
            <div className="totalen-wrapper">
              {renderTotalen()}
              {renderFooter()}
            </div>
          )}
        </div>
      );
    }

    return pages;
  };

  return (
    <div className="offerte-detail-container">
      {/* Portaal navigatie */}
      <div className="print-hide action-buttons">
        <button
          onClick={() => navigate(`/portaal/${token}`)}
          className="btn btn-secondary"
        >
          ← Terug naar portaal
        </button>
        <button onClick={() => window.print()} className="btn btn-primary">
          Afdrukken
        </button>
        {offerte.status === 'VERSTUURD' && (
          <button
            onClick={() => setShowGoedkeurModal(true)}
            className="btn btn-success"
            style={{ backgroundColor: '#16a34a' }}
          >
            Offerte Goedkeuren
          </button>
        )}
      </div>

      {/* Success message */}
      <Toast message={goedkeurSuccess} type="success" onClose={() => setGoedkeurSuccess(null)} />

      {/* Al goedgekeurd melding */}
      {offerte.status === 'GOEDGEKEURD' && offerte.goedgekeurd_op && (
        <div className="print-hide" style={{
          backgroundColor: '#dbeafe',
          border: '1px solid #3b82f6',
          color: '#1e40af',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          <strong>Deze offerte is goedgekeurd</strong>
          <p style={{ margin: '0.5rem 0 0 0' }}>
            Goedgekeurd door: {offerte.goedgekeurd_door}<br />
            Op: {new Date(offerte.goedgekeurd_op).toLocaleString('nl-NL')}
          </p>
        </div>
      )}

      {/* Render pages - identiek aan OfferteDetail */}
      {renderPages()}

      {/* Goedkeur Modal */}
      {showGoedkeurModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '1.5rem' }}>Offerte Goedkeuren</h2>

            <p style={{ marginBottom: '1rem' }}>
              U staat op het punt om offerte <strong>{offerte.offertenummer}</strong> goed te keuren
              voor een totaalbedrag van <strong>{formatCurrency(offerte.totaal)}</strong> (incl. BTW).
            </p>

            {goedkeurError && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #dc2626',
                color: '#dc2626',
                padding: '0.75rem',
                borderRadius: '4px',
                marginBottom: '1rem'
              }}>
                {goedkeurError}
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                Uw naam (voor akkoord):
              </label>
              <input
                type="text"
                value={goedkeurNaam}
                onChange={(e) => setGoedkeurNaam(e.target.value)}
                placeholder="Vul uw volledige naam in"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '1rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={voorwaardenAkkoord}
                  onChange={(e) => setVoorwaardenAkkoord(e.target.checked)}
                  style={{ marginTop: '0.25rem', width: '18px', height: '18px' }}
                />
                <span>
                  Ik ga akkoord met de offerte en de{' '}
                  <a href={`${getApiBaseUrl()}/api/v1/files/serve/Documenten/N/NJI/NJI Leveringsvoorwaarden.pdf?inline=true`} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>
                    algemene voorwaarden
                  </a>
                  . Ik begrijp dat dit een bindende overeenkomst is.
                </span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowGoedkeurModal(false);
                  setGoedkeurError(null);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Annuleren
              </button>
              <button
                onClick={handleGoedkeuren}
                disabled={goedkeurLoading}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: goedkeurLoading ? '#9ca3af' : '#16a34a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: goedkeurLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {goedkeurLoading ? 'Bezig...' : 'Bevestig Goedkeuring'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OffertePortaalView;
